/**
 * Wave Animation for Text
 * Adds a wave effect to text elements with the 'wave-animation' class
 */

(function () {
  'use strict';

  // Animation configuration constants
  const ANIMATION_CONFIG = {
    duration: 1.5, // Animation duration in seconds (slower = higher value)
    delayBetweenChars: 0.05, // Delay between each character in seconds
    iterationDelay: 3, // Delay between animation iterations in seconds
  };

  /**
   * Wraps each character in a span element
   * @param {HTMLElement} element - The element to process
   */
  function wrapCharacters(element) {
    const text = element.textContent;
    const chars = text.split('');
    const totalChars = chars.length;

    element.innerHTML = '';

    // Calculate total animation cycle time
    // Total cycle = wave duration + (delay per char * total chars) + iteration delay
    const totalCycleTime =
      ANIMATION_CONFIG.duration +
      ANIMATION_CONFIG.delayBetweenChars * totalChars +
      ANIMATION_CONFIG.iterationDelay;

    // Apply animation config to element
    element.style.setProperty(
      '--wave-duration',
      `${ANIMATION_CONFIG.duration}s`
    );
    element.style.setProperty(
      '--wave-char-delay',
      `${ANIMATION_CONFIG.delayBetweenChars}s`
    );
    element.style.setProperty('--wave-total-cycle', `${totalCycleTime}s`);
    element.style.setProperty('--wave-total-chars', totalChars);

    // Every character becomes its own inline-block so it can be animated
    // independently, and inline-blocks are atomic boxes the browser is free to
    // break a line between. Left flat, that means a heading can wrap mid-word
    // ("serve up goo / d vibes"). Grouping the characters of each word inside a
    // nowrap wrapper keeps breaks at spaces only, without changing the animation.
    let word = null;

    chars.forEach((char, index) => {
      const span = document.createElement('span');
      span.textContent = char;
      span.className = 'wave-char';

      // Indices stay continuous across the whole string, spaces included, so the
      // ripple travels at the same rate it did before words were introduced.
      span.style.setProperty('--char-index', index);

      // Spaces stay direct children of the element, with the same class names and
      // inline styles as before: faq_cel.css hangs a hard line break off
      // `.wave-chars-10` and that only works on a top-level box. Closing the
      // current word here is also what creates the break opportunity.
      if (char === ' ') {
        span.style.display = 'block';
        span.style.width = '0.25em';
        span.className = `wave-char wave-chars-${index}`;
        element.appendChild(span);
        word = null;
        return;
      }

      if (word === null) {
        word = document.createElement('span');
        word.className = 'wave-word';
        element.appendChild(word);
      }

      word.appendChild(span);
    });
  }

  /**
   * Initialize wave animation on elements
   */
  function initWaveAnimation() {
    const waveElements = document.querySelectorAll('.wave-animation');

    waveElements.forEach((element) => {
      // Skip if already initialized
      if (element.classList.contains('wave-animation--initialized')) {
        return;
      }

      wrapCharacters(element);
      element.classList.add('wave-animation--initialized');
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWaveAnimation);
  } else {
    initWaveAnimation();
  }

  // Re-initialize for Shopify theme editor
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initWaveAnimation);
    document.addEventListener('shopify:block:select', initWaveAnimation);
  }
})();